import type BaseDto from '../interface/IBaseDto';
import type {
    CustomBaseDataType,
    ExternalBaseDataType,
    InternalBaseDataType,
} from '../interface/IBaseDto';
import apply from './apply';
import type IMapProperty from './IMapProperty';
import mapping from './mapping';

/** IntProps = internal props, the consumer api, ExtProps = the external workspace api, CustomProps = linkables */
function create<
    ExtensionProperties extends ExternalBaseDataType,
    IntProperties extends InternalBaseDataType,
    CustomProperties extends CustomBaseDataType,
    PK extends keyof IntProperties,
>(
    primaryKey: PK,
    mappings: IMapProperty[],
    customProperties: CustomProperties,
    sourceObject?: Partial<ExtensionProperties>,
): BaseDto<PK, ExtensionProperties, IntProperties> & IntProperties & CustomProperties {
    const defaultProps = Object.fromEntries(
        mappings.map(item => [item.localName, item.defaultValue]),
    );

    const intProperties = mapping.mapToInternalProps<IntProperties, ExtensionProperties>(
        mappings,
        sourceObject,
    );

    const newEntity: BaseDto<PK, ExtensionProperties, IntProperties> &
        IntProperties &
        CustomProperties = {
        /* BaseDto fields */
        apply: (data: Partial<ExtensionProperties>) =>
            apply(primaryKey, mappings, customProperties, { ...newEntity, ...data }),

        flatten: () =>
            mapping.mapToExternalProps<IntProperties, ExtensionProperties>(mappings, newEntity),

        mappings: () => mappings,

        primaryKey: () => intProperties[primaryKey],

        /* Load default values */
        ...defaultProps,

        /* Convert Ext(external field definitions) to local field definitions */
        ...intProperties,

        /* Any custom fields passed in (will override above) */
        ...customProperties,
    };

    return newEntity;
}

export default create;
